/**
 * "Orders for one entity" — the search-results page scoped to a single driver,
 * customer, or dispatcher.
 *
 * The typeahead's Orders-by-Driver / Customers / Dispatchers rows are aggregates
 * (one line summarizing an entity's order count). Clicking one used to open V1
 * legacy CMS with a contlist URL; it now routes here as
 * `/searchresults?q=<exact identity>&ordersBy=<kind>&label=<row label>` and the
 * page loads that entity's orders as PRO rows in the TanTable.
 */

export const ORDERS_BY_KINDS = ['driver', 'customer', 'dispatcher'] as const;

export type OrdersByKind = (typeof ORDERS_BY_KINDS)[number];

/** Whether an `ordersBy` URL param is one we know how to scope by. */
export function isOrdersByKind(value: string | null | undefined): value is OrdersByKind {
  return !!value && (ORDERS_BY_KINDS as readonly string[]).includes(value);
}

/**
 * The `types` filter to send to /search/global. These are OPT-IN sub-searches —
 * the plain typeahead pass never runs them — so the string must match the
 * `requested(...)` gates in backend search.py exactly.
 */
export function entityOrdersType(ordersBy: string | null | undefined): string | null {
  return isOrdersByKind(ordersBy) ? `${ordersBy}_pros` : null;
}

/** Page heading, preferring the label the clicked dropdown row carried. */
export function entityOrdersTitle(
  ordersBy: string | null | undefined,
  label: string | null | undefined,
  value: string,
): string {
  if (label) return `Orders for ${label}`;
  // No &label= (bookmarked or hand-typed URL) — name the kind ourselves.
  const kind = isOrdersByKind(ordersBy)
    ? ordersBy.charAt(0).toUpperCase() + ordersBy.slice(1)
    : '';
  return `Orders for ${[kind, value].filter(Boolean).join(' ')}`;
}
